/**
 * When a trip's cached end address may be re-resolved.
 *
 * A trip end address used to be resolved once and then never revisited, so a
 * trip whose end moved afterwards kept the old address forever.
 *
 * The sequence: a vehicle stops in traffic for more than two minutes, the phone
 * sends MOTION_END, and the trip looks finished while its end address is still
 * empty. Anything displaying that trip reverse-geocodes the traffic stop and
 * persists it. The vehicle moves again inside the gateway's five minute stitch
 * window, so the same trip is re-opened, its end point advances to the real
 * destination, and the gateway resets `geo` to 0 to say the cached text no
 * longer matches. Nothing read `geo` - every client asked only whether the
 * address was EMPTY, and it was not.
 *
 * Measured on production: 85 of 1090 trips in ninety days were in that state,
 * the worst carrying an address 27km from the trip's real end. The coordinates
 * were never wrong; only the cached text.
 */

/** The gateway re-opens an ended trip for five minutes. Allow for clock skew. */
const SETTLE_WINDOW_MS = 6 * 60 * 1000;

/** The server's own answer to "does the cached text still match the end point". */
export function addressIsCurrent(trip) {
    const geo = trip?.geo;
    return geo === 1 || geo === true || geo === '1';
}

/**
 * Whether the trip has stopped moving for long enough that its end point is
 * final. Resolving an end that can still move is what produced the bad data,
 * so an unsettled trip is left showing coordinates until it settles.
 *
 * Unknown timing counts as settled, which preserves the previous behaviour for
 * anything that does not carry a usable time.
 */
export function hasSettled(trip, now = Date.now()) {
    const raw = trip?.time;
    if (!raw) return true;
    // Server timestamps are UTC and tz-naive; parsed as local they would be out
    // by the viewer's offset, which here decides whether a trip looks finished.
    const normalised = String(raw).trim().replace(' ', 'T');
    const startedAt = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(normalised)
        ? normalised
        : `${normalised}Z`);
    if (Number.isNaN(startedAt)) return true;

    const seconds = Number(trip?.duration);
    const endedAt = startedAt + (Number.isFinite(seconds) ? seconds * 1000 : 0);
    return now - endedAt > SETTLE_WINDOW_MS;
}

/** Which sides of this trip still need resolving, if any. */
export function addressWork(trip, now = Date.now()) {
    const needStart = !trip?.from;
    const settled = hasSettled(trip, now);
    // A cached end the server has marked out of date is worth replacing, but
    // only once the end point has stopped moving.
    const endIsStale = !!trip?.to && !addressIsCurrent(trip) && settled;
    const needEnd = (!trip?.to && settled) || endIsStale;

    return {
        needStart,
        needEnd,
        // Nothing to do, or nothing that can safely be done yet.
        skip: (!needStart && !needEnd) || (!trip?.to && !settled),
    };
}
